import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AnswersImportPayload,
  CatalogueItem,
  Participant,
  ParticipantAnswer,
  RemoteCredentials,
  RemoteRoomView,
  SharedNote,
} from "../types";
import {
  encryptedAnswerMessage,
  encryptedCustomMessage,
  encryptedNoteMessage,
  encryptedProfileMessage,
  importRemoteAnswers,
  RemoteRoomConnection,
} from "../lib/remote";
import { MAX_CUSTOM_ENTRIES } from "../../shared/protocol";

export function useRemoteRoom(credentials: RemoteCredentials | null) {
  const [view, setView] = useState<RemoteRoomView | null>(null);
  const [error, setError] = useState("");
  const connectionRef = useRef<RemoteRoomConnection | null>(null);
  const activeItemRef = useRef<string | null>(null);

  useEffect(() => {
    if (!credentials) return;
    activeItemRef.current = readRemotePosition(credentials);
    const connection = new RemoteRoomConnection(credentials);
    connectionRef.current = connection;

    const onSnapshot = (event: Event) => {
      const snapshot = (event as CustomEvent<RemoteRoomView>).detail;
      const activeItemId = activeItemRef.current ?? snapshot.activeItemId;
      activeItemRef.current = activeItemId;
      saveRemotePosition(credentials, activeItemId);
      setView((current) => ({
        ...snapshot,
        activeItemId,
        partnerActiveItemId:
          current ? current.partnerActiveItemId : snapshot.partnerActiveItemId,
        connected: true,
      }));
      try {
        connection.send({ type: "presence", itemId: activeItemId });
      } catch {
        // A following snapshot or selection will refresh ephemeral presence.
      }
    };
    const onStatus = (event: Event) => {
      const connected = (event as CustomEvent<boolean>).detail;
      setView((current) => (current ? { ...current, connected } : current));
    };
    const onPresence = (event: Event) => {
      const detail = (
        event as CustomEvent<{ seat: "A" | "B"; itemId: string | null }>
      ).detail;
      if (detail.seat !== credentials.seat) {
        setView((current) =>
          current
            ? { ...current, partnerActiveItemId: detail.itemId }
            : current,
        );
      }
    };
    const onError = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      setError(
        typeof detail === "string"
          ? detail
          : typeof detail === "object" &&
              detail !== null &&
              "message" in detail
            ? String(detail.message)
            : "The room could not apply that update.",
      );
    };
    const onConflict = (event: Event) => {
      const detail = (event as CustomEvent<{ message: string }>).detail;
      setError(detail.message);
    };
    const onTerminal = (event: Event) => {
      const terminalState = (
        event as CustomEvent<"expired" | "deleted">
      ).detail;
      setView((current) =>
        current ? { ...current, terminalState, connected: false } : current,
      );
      if (!view) {
        setError(
          terminalState === "expired"
            ? "This room has expired."
            : "This room was deleted.",
        );
      }
    };

    connection.addEventListener("snapshot", onSnapshot);
    connection.addEventListener("status", onStatus);
    connection.addEventListener("presence", onPresence);
    connection.addEventListener("error", onError);
    connection.addEventListener("conflict", onConflict);
    connection.addEventListener("terminal", onTerminal);
    void connection.connect();

    return () => {
      connection.removeEventListener("snapshot", onSnapshot);
      connection.removeEventListener("status", onStatus);
      connection.removeEventListener("presence", onPresence);
      connection.removeEventListener("error", onError);
      connection.removeEventListener("conflict", onConflict);
      connection.removeEventListener("terminal", onTerminal);
      connection.disconnect();
      connectionRef.current = null;
    };
  }, [credentials?.roomId, credentials?.seat, credentials?.capability]);

  const saveAnswer = useCallback(
    async (answer: ParticipantAnswer) => {
      if (!credentials) return;
      const message = await encryptedAnswerMessage(credentials, answer);
      connectionRef.current?.send(message);
      setView((current) =>
        current
          ? {
              ...current,
              ownAnswers: {
                ...current.ownAnswers,
                [answer.itemId]: {
                  ...answer,
                  version: answer.version + 1,
                  savedAt: Date.now(),
                },
              },
            }
          : current,
      );
    },
    [credentials],
  );

  const importAnswers = useCallback(
    async (payload: AnswersImportPayload) => {
      if (!credentials || !view) return;
      if (!view.connected) {
        throw new Error("You are offline. Reconnect before importing answers.");
      }
      const currentCustomIds = new Set(
        view.customEntries.map((entry) => entry.id),
      );
      const customEntries = payload.customEntries.filter(
        (entry) => !currentCustomIds.has(entry.id),
      );
      const answers = payload.answers.map((answer) => ({
        ...answer,
        version: view.ownAnswers[answer.itemId]?.version ?? 0,
      }));
      if (view.customEntries.length + customEntries.length > MAX_CUSTOM_ENTRIES) {
        throw new Error(
          `This import would exceed the ${MAX_CUSTOM_ENTRIES}-custom-term room limit.`,
        );
      }
      await importRemoteAnswers(credentials, {
        answers,
        customEntries,
      });

      const now = Date.now();
      setView((current) => {
        if (!current) return current;
        const knownCustomIds = new Set(
          current.customEntries.map((entry) => entry.id),
        );
        return {
          ...current,
          ownAnswers: {
            ...current.ownAnswers,
            ...Object.fromEntries(
              answers.map((answer) => [
                answer.itemId,
                {
                  ...answer,
                  savedAt: now,
                  updatedAt: now,
                  version: answer.version + 1,
                },
              ]),
            ),
          },
          customEntries: [
            ...current.customEntries,
            ...customEntries
              .filter((entry) => !knownCustomIds.has(entry.id))
              .map((entry) => ({ ...entry, locked: true })),
          ],
        };
      });
    },
    [credentials, view],
  );

  const saveNote = useCallback(
    async (note: SharedNote) => {
      if (!credentials) return;
      connectionRef.current?.send(
        await encryptedNoteMessage(credentials, note),
      );
      setView((current) =>
        current
          ? {
              ...current,
              notes: {
                ...current.notes,
                [note.itemId]: {
                  ...note,
                  version: note.version + 1,
                  updatedAt: Date.now(),
                },
              },
            }
          : current,
      );
    },
    [credentials],
  );

  const saveProfile = useCallback(
    async (profile: Participant) => {
      if (!credentials || !view) return;
      connectionRef.current?.send(
        await encryptedProfileMessage(
          credentials,
          profile,
          view.profileVersions[credentials.seat],
        ),
      );
    },
    [credentials, view],
  );

  const addCustom = useCallback(
    async (item: CatalogueItem) => {
      if (!credentials) return;
      connectionRef.current?.send(
        await encryptedCustomMessage(credentials, item, "custom:create"),
      );
    },
    [credentials],
  );

  const updateCustom = useCallback(
    async (item: CatalogueItem) => {
      if (!credentials) return;
      connectionRef.current?.send(
        await encryptedCustomMessage(
          credentials,
          item,
          "custom:update",
          item.customVersion ?? 0,
        ),
      );
    },
    [credentials],
  );

  const deleteCustom = useCallback((item: CatalogueItem) => {
    connectionRef.current?.send({
      type: "custom:delete",
      entryId: item.id,
      version: item.customVersion ?? 0,
    });
  }, []);

  const setActiveItem = useCallback(
    (itemId: string) => {
      activeItemRef.current = itemId;
      if (credentials) saveRemotePosition(credentials, itemId);
      setView((current) =>
        current ? { ...current, activeItemId: itemId } : current,
      );
      try {
        connectionRef.current?.send({ type: "presence", itemId });
      } catch {
        // Presence is intentionally ephemeral; reconnect handles the next update.
      }
    },
    [credentials],
  );

  const deleteRoom = useCallback(() => {
    connectionRef.current?.send({ type: "room:delete" });
  }, []);

  const resetExpiry = useCallback(() => {
    try {
      connectionRef.current?.send({ type: "room:expiry:reset" });
      return true;
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "The room expiry could not be reset.",
      );
      return false;
    }
  }, []);

  return {
    view,
    error,
    setError,
    saveAnswer,
    importAnswers,
    saveNote,
    saveProfile,
    addCustom,
    updateCustom,
    deleteCustom,
    setActiveItem,
    deleteRoom,
    resetExpiry,
  };
}

function remotePositionKey(credentials: RemoteCredentials) {
  return `feel-scene:position:${credentials.roomId}:${credentials.seat}`;
}

function readRemotePosition(credentials: RemoteCredentials) {
  try {
    return localStorage.getItem(remotePositionKey(credentials));
  } catch {
    return null;
  }
}

function saveRemotePosition(
  credentials: RemoteCredentials,
  itemId: string,
) {
  try {
    localStorage.setItem(remotePositionKey(credentials), itemId);
  } catch {
    // Position recovery is helpful but must never block the room.
  }
}
